import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, UserRound, ShieldCheck } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { Logo } from "@/components/common/Logo";
import { LanguageSwitcher } from "@/components/layout/LanguageSwitcher";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { key: "solutions", path: "/solutions" },
  { key: "sectors", path: "/sectors" },
  { key: "projects", path: "/projects" },
  { key: "hosysAi", path: "/hosys-ai" },
  { key: "insights", path: "/insights" },
  { key: "company", path: "/company" },
  { key: "contact", path: "/contact" },
];

const MOBILE_EXTRA = [
  { key: "clientArea", path: "/client" },
];

export function Navbar() {
  const { lang, t } = useI18n();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const L = (p) => `/${lang}${p}`;

  // Smart user icon: admins → /admin, clients → /client, guests → /login
  const userDestination = !user ? L("/login") : user.role === "admin" ? L("/admin") : L("/client");
  const userLabel = !user ? t("nav.clientArea") : user.role === "admin" ? "Panell d'admin" : t("nav.clientArea");
  const UserIcon = user?.role === "admin" ? ShieldCheck : UserRound;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const isActive = (p) => location.pathname === L(p);

  return (
    <>
      <header
        data-testid="main-navbar"
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
          scrolled ? "backdrop-blur-xl bg-black/70 border-b border-white/10" : "bg-transparent border-b border-transparent"
        }`}
      >
        <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
          <Link to={L("")} data-testid="nav-logo-link" aria-label="HO-SYS home">
            <Logo />
          </Link>

          <div className="hidden lg:flex items-center gap-1">
            {NAV.map((item) => (
              <Link
                key={item.key}
                to={L(item.path)}
                data-testid={`nav-link-${item.key}`}
                className={`relative px-4 py-2 text-sm font-medium transition-colors ${
                  isActive(item.path) ? "text-white" : "text-white/60 hover:text-white"
                }`}
              >
                {t(`nav.${item.key}`)}
                {isActive(item.path) && (
                  <motion.span layoutId="nav-underline" className="absolute left-4 right-4 -bottom-0.5 h-px bg-neon" />
                )}
              </Link>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <Link to={userDestination} data-testid="nav-user-icon" aria-label={userLabel} title={userLabel}
              className={`p-2.5 rounded-full border transition-colors ${user?.role === "admin" ? "border-neon/50 text-neon hover:bg-neon/10" : "border-white/15 text-white/70 hover:text-neon hover:border-neon/60"}`}>
              <UserIcon className="w-4 h-4" />
            </Link>
            <LanguageSwitcher />
            <Link
              to={L("/demo")}
              data-testid="nav-cta-demo"
              className="group inline-flex items-center gap-1.5 rounded-full bg-neon px-5 py-2.5 text-sm font-semibold text-black hover:bg-white transition-colors"
            >
              {t("nav.demo")}
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>

          <div className="flex items-center gap-2 lg:hidden">
            <LanguageSwitcher />
            <button
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
              onClick={() => setOpen((v) => !v)}
              className="p-2 text-white"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-black/95 backdrop-blur-xl lg:hidden pt-[72px]"
          >
            <div className="flex flex-col p-6 gap-1">
              {NAV.map((item, i) => (
                <motion.div
                  key={item.key}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                >
                  <Link
                    to={L(item.path)}
                    data-testid={`mobile-nav-link-${item.key}`}
                    className="block py-4 text-2xl font-display font-semibold text-white border-b border-white/10"
                  >
                    {t(`nav.${item.key}`)}
                  </Link>
                </motion.div>
              ))}
              {MOBILE_EXTRA.map((item, i) => (
                <motion.div
                  key={item.key}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: (NAV.length + i) * 0.05 }}
                >
                  <Link
                    to={L(item.path)}
                    data-testid={`mobile-nav-link-${item.key}`}
                    className="block py-4 text-2xl font-display font-semibold text-white border-b border-white/10"
                  >
                    {t(`nav.${item.key}`)}
                  </Link>
                </motion.div>
              ))}
              <Link
                to={L("/demo")}
                data-testid="mobile-nav-cta"
                className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-neon px-5 py-4 text-base font-semibold text-black"
              >
                {t("nav.demo")} <ArrowUpRight className="w-5 h-5" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
